/**
 * אבטחה ויומן ביקורת — the office reading its OWN events. 04 §8.2.
 *
 * The scoping is the design: the tenant's own `tenant_admin` / `tenant_owner` read THIS TENANT'S
 * events, through a TENANT-scoped route, under `audit.read`. Cross-tenant reading is a PLATFORM
 * capability on the other surface, and there is no control on this screen that could widen the
 * scope — no tenant field, no "all customers" toggle.
 *
 * ── WHY THE DENIED ROWS ARE HERE ────────────────────────────────────────────────────────────────
 *
 * An audit view that shows only what succeeded cannot answer "who tried". Denials are rows with the
 * same standing as allowances, and their Hebrew comes from the same mapping the user themselves saw
 * — `denialText.ts` — so the administrator reading the log and the person who was refused are
 * looking at the same sentence rather than at a code and its translation.
 *
 * ── AND WHY SUPPORT GRANTS ARE NEVER FILTERED OUT ───────────────────────────────────────────────
 *
 * A support grant is the only bridge between the platform and a tenant's data, every call under it
 * is audited ALWAYS (04 §13.1.5 / §6.4), and the customer may always revoke it from their own
 * console. So a grant row is marked, in words, and an office must never have to ask whether somebody
 * from outside was inside their data.
 *
 * OA-0001 §3.2 is the whole point of the screen: GL-POINT supplies the authorization system, the
 * customer OPERATES it — and a system you cannot audit is not one you operate.
 */

import { useCallback, useState } from 'react';

import { AppShell } from '../shell/AppShell';
import { useWorkContext } from '../shell/ContextProvider';
import { DataTable } from '../components/DataTable';
import { StatusChip } from '../components/StatusChip';
import { SurfaceNotice } from '../components/SurfaceNotice';
import type { Column } from '../components/DataTable';
import type { AuditEventSummary } from '../api/contract';
import { auditActorKindHe, auditOutcomeHe, formatInstantHe } from '../api/adminVocabulary';
import { denialTextFor } from '../api/denialText';
import { callOperation } from '../api/operations';
import { ServerRead } from '../components/ServerRead';
import { ActionFailureNotice } from '../components/ActionFailureNotice';
import { classifyActionError, type ActionFailure } from '../api/actionOutcome';
import { listOf, useServerData } from '../api/useServerData';
import { routes } from '../routes';

const TITLE_HE = 'אבטחה ויומן ביקורת';

/**
 * CM-012 / CM-058. The office's own support-access controls.
 *
 * This screen used to state, in Hebrew, that the office may revoke a support grant at any moment —
 * beside no control and no route. The sentence was true of the design and false of the product. It
 * now calls the operations that make it true.
 */
export function TenantSecurityScreen(): JSX.Element {
  const ctx = useWorkContext();
  const [busy, setBusy] = useState<string | null>(null);
  const [writeFailure, setWriteFailure] = useState<ActionFailure | null>(null);

  /*
   * THE GRANTS READ GOES THROUGH `useServerData`, LIKE EVERY OTHER READ ON THIS SCREEN.
   *
   * It did not. It was a `useState` list filled by an effect that did `void loadGrants()`, and the
   * two consequences were both bad and one was invisible:
   *
   *   · THE FALSE ALIBI, on the panel whose subject is exactly that. A read that failed left the
   *     list empty, and the empty branch renders *"there is currently no active support grant for
   *     this office"* -- a positive security statement about who can reach the customer's data,
   *     produced by a request that never answered. This screen's own header calls that the one
   *     distinction it must never confuse; the audit table below honoured it and the panel above
   *     did not.
   *   · AND THE REJECTION FLOATED. `void` on a promise that throws is an unhandled rejection: in a
   *     browser it reaches the console and nothing else, and in the suite it surfaced against
   *     whichever test happened to be running when it landed -- which is how it was found, as an
   *     intermittent failure in a file that passes on its own.
   *
   * `useServerData` keeps loading, loaded and failed apart, and `ServerRead` keeps them apart on
   * screen. There is nothing here for a second mechanism to do better.
   */
  const readGrants = useCallback(() => callOperation('security.support_grant.read', {}), []);
  const grantsRead = useServerData(readGrants);

  /*
   * AND THE WRITES CATCH. Same taxonomy as every other write in the app -- `classifyActionError`
   * once, `ActionFailureNotice` to render it -- so a refused revocation says it was refused rather
   * than leaving the button stuck and the grant standing with no explanation.
   */
  const consent = useCallback(
    async (grantUid: string): Promise<void> => {
      setBusy(grantUid);
      setWriteFailure(null);
      try {
        await callOperation('security.support_grant.consent', {
          params: { grant_uid: grantUid },
          body: {},
        });
        grantsRead.reload();
      } catch (err: unknown) {
        setWriteFailure(classifyActionError(err));
      } finally {
        setBusy(null);
      }
    },
    [grantsRead],
  );

  const revoke = useCallback(
    async (grantUid: string, reason: string): Promise<void> => {
      setBusy(grantUid);
      setWriteFailure(null);
      try {
        await callOperation('security.support_grant.revoke', {
          params: { grant_uid: grantUid },
          body: { reason },
        });
        grantsRead.reload();
      } catch (err: unknown) {
        setWriteFailure(classifyActionError(err));
      } finally {
        setBusy(null);
      }
    },
    [grantsRead],
  );

  if (ctx.surface !== 'TENANT') {
    return <SurfaceNotice titleHe={TITLE_HE} requires="TENANT" actual={ctx.surface} />;
  }

  /*
   * READ FROM THE SERVER. **Phase 2 §6, vertical C.**
   *
   * A tenant's own audit trail is the control that makes support access acceptable at all: 04 §8.2
   * gives the office the ability to see every crossing into its data, and an office reading
   * development events was being shown a trail that described nobody. This is the one screen where
   * an empty list and a refused read are furthest apart — "nobody entered your data" against "we
   * cannot tell you who did".
   */
  const read = useCallback(() => callOperation('tenant.audit.read', {}), []);
  const auditRead = useServerData(read);
  const events: readonly AuditEventSummary[] = listOf(auditRead.result, 'events');
  const underSupportGrant = events.filter((e) => e.supportGrantUid !== null).length;

  const columns: readonly Column<AuditEventSummary>[] = [
    {
      key: 'when',
      headerHe: 'מתי',
      render: (e) => <span dir="ltr">{formatInstantHe(e.occurredAtUtc) ?? '—'}</span>,
    },
    {
      key: 'who',
      headerHe: 'מי',
      render: (e) => (
        <span className="company-cell">
          <span className="company-cell__name">{e.actorDisplayName ?? auditActorKindHe(e.actorKind)}</span>
          {e.supportGrantUid === null ? null : (
            <span className="company-cell__id">בוצע תחת הרשאת תמיכה</span>
          )}
        </span>
      ),
    },
    { key: 'what', headerHe: 'מה', render: (e) => e.actionHe },
    {
      key: 'outcome',
      headerHe: 'תוצאה',
      render: (e) => {
        const term = auditOutcomeHe(e.outcome);
        return <StatusChip labelHe={term.labelHe} tone={term.tone} />;
      },
    },
    {
      key: 'why',
      headerHe: 'הסיבה לדחייה',
      // The same Hebrew the refused user saw. Never the machine code (09 §9.1 V3).
      render: (e) => (e.denyReason === null ? null : denialTextFor(e.denyReason).titleHe),
    },
  ];

  return (
    <AppShell
      titleHe={TITLE_HE}
      backToParent={{ labelHe: 'ניהול המשרד', href: routes.tenantAdmin }}
      provenanceHe="היומן מציג את אירועי המשרד הזה בלבד. אירועים של משרדים אחרים אינם נגישים ממנו בשום דרך."
    >
      {underSupportGrant === 0 ? null : (
        <section className="notice" data-tone="warn">
          <h2 className="notice__title">בוצעו פעולות תחת הרשאת תמיכה</h2>
          <p className="notice__body">
            {`${underSupportGrant} מהפעולות ביומן בוצעו בידי תמיכת הפלטפורמה תחת הרשאה שניתנה למשרד. כל פעולה כזו נרשמת תמיד.`}
          </p>
        </section>
      )}

      {/* CM-012. The grants themselves, and the two acts the office may take on them. */}
      <section className="panel">
        <h2 className="panel__title">הרשאות תמיכה למשרד הזה</h2>
        {writeFailure === null ? null : <ActionFailureNotice failure={writeFailure} />}
        <ServerRead result={grantsRead.result} whatHe="את הרשאות התמיכה" skeletonRows={2}>
          {(data) =>
            data.grants.length === 0 ? (
              /*
               * ONLY REACHABLE FROM A READ THAT SUCCEEDED. `ServerRead` hands this branch the DATA,
               * so there is no value for it to render when the request failed -- which is what makes
               * this sentence a statement about the office's grants rather than about the request.
               */
              <p className="panel__empty">אין כרגע הרשאת תמיכה פעילה למשרד הזה.</p>
            ) : (
              <ul className="grant-list">
                {data.grants.map((grant) => (
                  <li key={grant.grantUid} className="grant-list__item">
                    <span className="grant-list__reason">{grant.reason}</span>
                    <span className="grant-list__window" dir="ltr">
                      {`${formatInstantHe(grant.startsAtUtc) ?? '—'} – ${formatInstantHe(grant.expiresAtUtc) ?? '—'}`}
                    </span>
                    {grant.requiresConsent && grant.consentGivenAtUtc === null ? (
                      <button
                        type="button"
                        disabled={busy === grant.grantUid}
                        onClick={() => void consent(grant.grantUid)}
                      >
                        אישור ההרשאה
                      </button>
                    ) : null}
                    <button
                      type="button"
                      disabled={busy === grant.grantUid || grant.revokedAtUtc !== null}
                      onClick={() => void revoke(grant.grantUid, 'בוטל בידי המשרד')}
                    >
                      ביטול ההרשאה
                    </button>
                  </li>
                ))}
              </ul>
            )
          }
        </ServerRead>
      </section>

      <ServerRead result={auditRead.result} whatHe="את יומן הביקורת" skeletonRows={10}>
        {(data) => (
          <DataTable<AuditEventSummary>
            captionHe="אירועים אחרונים"
            columns={columns}
            rows={data.events}
            rowKey={(e) => e.eventUid}
            filterSummaryHe={`מציג ${data.events.length} אירועים אחרונים במשרד — כולל פעולות שנדחו`}
            // "Nothing has happened in your office" and "we cannot tell you what happened" are the
            // two sentences this screen must never confuse. It is the office's own evidence that
            // nobody entered its data, and an outage rendered as an empty log is a false alibi.
            emptyStateHe="לא נרשמו עדיין אירועים במשרד."
          />
        )}
      </ServerRead>

      <div className="actions-row">
        <a className="btn" href={routes.mfaEnrolment}>
          אימות דו-שלבי
        </a>
      </div>
    </AppShell>
  );
}
